import {AfterViewInit, Component, ElementRef, inject, NgZone, OnDestroy, OnInit, ViewChild, ChangeDetectionStrategy} from '@angular/core';
import {
  CheckResultControllerService,
  DashboardControllerService,
  FileDashboardSummary,
  Project,
  ProjectFileControllerService,
  QualityCheck,
  SimpleCheck,
  Task,
  TaskDashboardData,
  TasksDashboardResponse,
  User
} from "../../api";
import {
  faBadgeCheck,
  faBars,
  faBuilding,
  faChartSimple,
  faChevronDown,
  faChevronLeft,
  faChevronRight,
  faCircleUser,
  faClipboardCheck,
  faClipboardList,
  faCube,
  faEllipsisVertical,
  faFileCircleCheck,
  faFolderOpen,
  faInfo,
  faInfoCircle,
  faListCheck,
  faMemoCircleCheck
} from "@fortawesome/pro-solid-svg-icons";
import {StateConfigService} from "../../services/state/state-config.service";
import {Router} from "@angular/router";
import {LphPipe} from "../../pipes/lph.pipe";
import {CircleState} from "../util/design/circle-state/circle-state.component";
import {CheckItem} from "../../quality-api";
import {DatePipe} from "@angular/common";
import {LoiElement, LoiProperties} from "../checks/bimCheck/components/loi-check-report/loi-check-report.component";
import {DashboardCardComponent} from './dashboard-card/dashboard-card.component';
import {FaIconComponent} from '@fortawesome/angular-fontawesome';
import {
  DashboardProjectInformationComponent
} from './dashboard-project-information/dashboard-project-information.component';
import {DashboardTasksComponent} from './dashboard-tasks/dashboard-tasks.component';
import {DashboardCheckMetricsComponent} from './dashboard-check-metrics/dashboard-check-metrics.component';
import {DashboardCheckListComponent} from './dashboard-check-list/dashboard-check-list.component';
import {DashboardFilesComponent} from './dashboard-files/dashboard-files.component';
import {faIfc} from "../util/icons/custom-icons";
import {CheckReportService} from "../../services/checks/check-report.service";
import {AllInCheckNavigationService} from "../../services/checks/all-in-check-navigation.service";
import {Subscription} from "rxjs";

/**
 * Dashboard for User
 */
@Component({
    selector: 'app-dashboard',
    templateUrl: './dashboard.component.html',
    styleUrls: ['./dashboard.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [DashboardCardComponent, FaIconComponent, DashboardProjectInformationComponent, DashboardTasksComponent, DashboardCheckMetricsComponent, DashboardCheckListComponent, DashboardFilesComponent]
})
export class DashboardComponent implements OnInit, AfterViewInit, OnDestroy {
  stateConfigService = inject(StateConfigService);
  private dashboardController = inject(DashboardControllerService);
  private projectFileController = inject(ProjectFileControllerService);
  private checkResultController = inject(CheckResultControllerService);
  private checkReportService = inject(CheckReportService);
  private allInCheckNavigation = inject(AllInCheckNavigationService);
  /** 1536 px (`2xl`) minus 272 px Navigationsleiste, siehe MAIN_SCREENS_PX in tailwind.config.js. */
  private static readonly THREE_COLUMN_MIN_WIDTH_PX = 1536 - 272;
  private router = inject(Router);
  private lphPipe = inject(LphPipe);

  fileSummary: FileDashboardSummary | null = null;
  filesLoaded: boolean = false;


  protected readonly faBars = faBars;
  protected readonly Task = Task;
  protected readonly undefined = undefined;
  protected readonly faBuilding = faBuilding;
  protected readonly faCircleUser = faCircleUser;
  protected readonly faFileCircleCheck = faFileCircleCheck;
  protected readonly faListCheck = faListCheck;
  protected readonly faChartSimple = faChartSimple;
  protected readonly faChevronLeft = faChevronLeft;
  protected readonly faChevronRight = faChevronRight;
  protected readonly faClipboardList = faClipboardList;
  protected readonly faFolderOpen = faFolderOpen;

  hover: boolean = false;
  loaded: boolean = false;

  // Individuelle Data + Loading pro Kachel
  taskData: TasksDashboardResponse | null = null;
  tasksLoaded: boolean = false;

  /**
   * Die Eingaben der Task-Kachel, einmal berechnet statt bei jedem Zugriff.
   *
   * Vorher waren das Getter, die ihr Array jedes Mal neu erzeugt haben. Angular
   * vergleicht Inputs ueber die Referenz - ein frisches Array gilt damit in
   * *jedem* Change-Detection-Durchlauf als geaendert, die Kachel wurde also
   * endlos neu geprueft. Im Development-Modus laeuft danach `checkNoChanges`
   * und findet wieder ein anderes Array; das ist die klassische Quelle von
   * NG0100 und hat das Dashboard bis zum Stillstand ausgebremst - beobachtet
   * bei Navigation, HMR-Refresh und jedem Klick, mit Durchlaeufen, die von
   * 154 ms auf ueber 4 Sekunden anwuchsen.
   */
  basicTasks: number[] = [0, 0, 0, 0];
  allTasksList: TaskDashboardData[] = [];

  /** Ausgeführte Prüfläufe des Projekts (inkl. myChecks) für die Prüfungs-Kachel. */
  checkRuns: SimpleCheck[] = [];
  checkRunsLoaded: boolean = false;

  tasks: any[] = [];
  taskMap: Map<Task.StateEnum, number> = new Map<Task.StateEnum, number>();
  totalTasks: number = 0;

  taskTemp: any[] = [
    {
      "name": "OPEN",
      "value": 0
    },
    {
      "name": "PROCESS",
      "value": 0
    },
    {
      "name": "DONE",
      "value": 0
    },
    {
      "name": "VERIFIED",
      "value": 0
    }
  ];

  dashboardCards = [
    {
      title: 'HOAI-Check',
      icon: this.faListCheck,
      navigateFunction: () => this.navigateToHOAICheck(),
      message: 'Es wurden keine HAOI-Checks erstellt',
    },
    {
      title: 'Qualitäts-Check',
      icon: this.faChartSimple,
      navigateFunction: () => this.navigateToQualityCheck(),
      message: 'Es wurden keine Qualitäts-Checks erstellt',
    },
    {
      title: 'BIM Basis-Check',
      icon: this.faFileCircleCheck,
      navigateFunction: () => this.navigateToLOICheck(),
      message: 'Es wurden keine BIM Basis-Checks erstellt',
    },
    {
      title: 'LOI-Check',
      icon: this.faFileCircleCheck,
      navigateFunction: () => this.navigateToLOICheck(),
      message: 'Es wurden keine LOI-Checks erstellt',
    },
  ];

  assignments = [
    {label: 'Offen', colorClass: 'text-secondary-45', opacity: ''},
    {label: 'In Bearbeitung', colorClass: 'text-primary', opacity: 'opacity-40'},
    {label: 'Erledigt', colorClass: 'text-primary', opacity: 'opacity-80'},
    {label: 'Verifiziert', colorClass: 'text-primary', opacity: ''}
  ];


  users!: User[];
  user!: User | undefined;
  lastChecks: Array<any> | undefined = [];
  selectedCheck: any | undefined = undefined;
  checks: any[][] = [];
  numberOfIFCChecks = 0;
  states = ["OPEN", "PROCESS", "DONE", "VERIFIED"]
  taskArray: any[] = [];

  get project(): Project {
    return this.stateConfigService.selectedProject;
  }

  get isBim(){
    return this.stateConfigService.selectedProject.isBIM;
  }

  /**
   * Uebernimmt die Task-Antwort und leitet die Kachel-Eingaben einmal ab.
   *
   * Der einzige Ort, an dem `basicTasks` und `allTasksList` neu entstehen -
   * dadurch bleiben ihre Referenzen zwischen zwei Change-Detection-Durchlaeufen
   * stabil, und die Kachel wird nur noch geprueft, wenn sich wirklich etwas
   * geaendert hat.
   */
  private applyTaskData(data: TasksDashboardResponse | null): void {
    this.taskData = data;

    const tasks = data?.tasks;
    this.basicTasks = tasks
      ? [
          tasks['OPEN']?.length ?? 0,
          tasks['PROCESS']?.length ?? 0,
          tasks['DONE']?.length ?? 0,
          tasks['VERIFIED']?.length ?? 0
        ]
      : [0, 0, 0, 0];

    // `?? []` wuerde bei fehlenden Daten wieder ein neues Array je Aufruf
    // liefern - hier entsteht es genau einmal.
    this.allTasksList = data?.myTasks ?? [];
  }

  private checkCompletedSub: Subscription | null = null;
  /**
   * Ab `main-2xl` hat das Dashboard drei Spalten. Dann stehen unten zwei Kacheln – Kennzahlen und
   * Prüflaufliste getrennt – statt einer kombinierten. Muss zum `main-2xl:`-Umbruch im Template
   * passen: gemessen wird deshalb die Breite des Inhaltsbereichs, nicht die des Fensters – sonst
   * bleibt bei angedocktem chatBIC das dreispaltige Layout stehen.
   */
  threeColumnLayout = false;
  private zone = inject(NgZone);
  private layoutObserver?: ResizeObserver;
  @ViewChild('dashboardGrid') private dashboardGrid?: ElementRef<HTMLElement>;

  ngOnInit(): void {
    const projectId = this.stateConfigService.getProjectId();

    // Dateien
    this.projectFileController.getDashboardSummary(projectId).subscribe({
      next: (summary) => {
        this.fileSummary = summary;
        this.filesLoaded = true;
      },
      error: () => {
        this.filesLoaded = true;
      }
    });

    // Tasks
    this.dashboardController.getDashboardTasks(projectId).subscribe({
      next: (data) => {
        this.applyTaskData(data);
        this.tasksLoaded = true;
      },
      error: () => {
        this.tasksLoaded = true;
      }
    });

    // Prüfläufe (myChecks, All-In, All-Right) – dieselbe Quelle wie die Berichte-Übersicht
    this.loadCheckRuns();

    // Sobald ein Check per SSE als fertig gemeldet wird, die Kachel nachziehen
    this.checkCompletedSub = this.stateConfigService.checkCompleted$.subscribe(() => this.loadCheckRuns());
  }

  ngAfterViewInit(): void {
    const grid = this.dashboardGrid?.nativeElement;
    if (!grid) return;
    this.layoutObserver = new ResizeObserver(entries => {
      const width = entries[0]?.borderBoxSize?.[0]?.inlineSize ?? grid.getBoundingClientRect().width;
      const threeColumns = width >= DashboardComponent.THREE_COLUMN_MIN_WIDTH_PX;
      if (threeColumns !== this.threeColumnLayout) {
        this.zone.run(() => this.threeColumnLayout = threeColumns);
      }
    });
    this.layoutObserver.observe(grid);
  }

  ngOnDestroy(): void {
    this.checkCompletedSub?.unsubscribe();
    this.layoutObserver?.disconnect();
  }

  private loadCheckRuns(): void {
    const projectId = this.stateConfigService.getProjectIdOrUndefined();
    if (!projectId) {
      this.checkRuns = [];
      this.checkRunsLoaded = true;
      return;
    }

    this.checkResultController.getCheckResultsAsSimpleChecks(projectId, 'ALL').subscribe({
      next: (checks) => {
        this.checkRuns = checks;
        this.checkRunsLoaded = true;
      },
      error: () => {
        this.checkRuns = [];
        this.checkRunsLoaded = true;
      }
    });
  }

  /** Öffnet den Bericht eines Prüflaufs – Zielroute abhängig von Prüftyp und Status. */
  openCheckRun(check: SimpleCheck): void {
    const route = this.checkReportService.resolveCheckRoute(check, this.project.id!);
    if (route) {
      this.router.navigateByUrl(route);
    }
  }

  transformDateToString(date: Date): string {
    let datePipe = new DatePipe('de-DE');
    return <string>datePipe.transform(date, "dd.MM.yyyy HH:mm");
  }

  getSizeOfFolder(size: number): string {
    if (this.calculateSizeInGB(size) < 1) {
      return this.calculateSizeInMB(size).toFixed(2) + ' MB'
    } else {
      return this.calculateSizeInGB(size).toFixed(2) + ' GB'
    }
  }

  getNumberOfTasks(): number {
    let sum = 0;
    if (this.taskData?.tasks) {
      for (let state of this.states) {
        sum += this.taskData.tasks[state]?.length ?? 0;
      }
    }
    return sum;
  }

  getPercentageOfTasks(state: string): string {
    if (this.taskData?.tasks) {
      if (this.getNumberOfTasks() == 0) {
        return "0";
      }
      return ((this.taskData.tasks[state]?.length ?? 0) / this.getNumberOfTasks() * 100).toFixed(0);
    } else {
      return "0";
    }
  }


  calculateSizeInMB(size: number): number {
    return size / 1024 / 1024;
  }

  calculateSizeInGB(size: number): number {
    return size / 1024 / 1024 / 1024;
  }


  calculateArraysForChecks() {
    this.checks = [];
    for (let check of this.lastChecks!) {
      if (check.checkType == "HOAI") {
        if (check.checkResults == undefined || check.checkResults.length == 0) {
          let array: any[] = [{
            name: "Erfüllt",
            value: 0
          }, {
            name: "Teilweise Erfüllt",
            value: 0
          }, {
            name: "Nicht Erfüllt",
            value: 100
          }];
          this.checks.push(array);
        } else {
          let array: any[] = [{
            name: "Erfüllt",
            value: Math.round((check.checkResults?.reduce((acc: number, cur: {
              succeed: any;
            }) => cur.succeed ? acc + 1 : acc, 0) ?? 0) / check.partialServiceNames?.length * 100)
          }, {
            name: "Teilweise Erfüllt",
            value: 0
          }, {
            name: "Nicht Erfüllt",
            value: 100 - Math.round((check.checkResults?.reduce((acc: number, cur: {
              succeed: any;
            }) => cur.succeed ? acc + 1 : acc, 0) ?? 0) / check.partialServiceNames?.length * 100)
          }];
          this.checks.push(array);
        }
      } else if (check.checkType == "IFC") {
        if (check.results.startsWith("[")) {
          let result = JSON.parse(check.results);

          let array: any[] = [{
            name: "Erfüllt",
            value: this.medianOfPercentages(result)
          }, {
            name: "Teilweise Erfüllt",
            value: 0
          }, {
            name: "Nicht Erfüllt",
            value: 100 - this.medianOfPercentages(result)
          }];
          this.checks.push(array);
        } else {
          let array: any[] = [{
            name: "Erfüllt",
            value: 0
          }, {
            name: "Teilweise Erfüllt",
            value: 100
          }, {
            name: "Nicht Erfüllt",
            value: 0
          }];
          this.checks.push(array);
        }

      } else {
        let positions = this.positions(check);
        let redState = positions.filter((position: {
          state: CircleState;
        }) => position.state == CircleState.RED).length;
        let yellowState = positions.filter((position: {
          state: CircleState;
        }) => position.state == CircleState.YELLOW).length;
        let greenState = positions.filter((position: {
          state: CircleState;
        }) => position.state == CircleState.GREEN).length;

        let array: any[] = [{
          name: "Erfüllt",
          value: Math.round(greenState / positions.length * 100)
        }, {
          name: "Teilweise Erfüllt",
          value: Math.round(yellowState / positions.length * 100)
        }, {
          name: "Nicht Erfüllt",
          value: Math.round(redState / positions.length * 100)
        }];

        this.checks.push(array);
      }
    }
  }

  positions(check: QualityCheck): any[] {
    return Object.entries(check.results!).map(([key, value]) => {
      const data = value != "" ? (JSON.parse(value as string).ifcPositions as CheckItem[]) : []
      return {
        rule: key,
        result: data,
        state: this.stateOfResult(data)
      }
    });
  }

  stateOfResult(value: CheckItem[]): CircleState {
    if (value?.length === 0) {
      return CircleState.YELLOW;
    }
    // if (value?.filter(result => result.check.errors.length > 0).length > 0) {
    //   return CircleState.RED;
    // }
    // if (value?.filter(result => result.check.warnings.length > 0).length > 0) {
    //   return CircleState.YELLOW;
    // }
    return CircleState.GREEN;
  }

  medianOfPercentages(json: LoiElement[]): number {
    let value = Object.values(json).map(element => this.medianOfPercentagesElement(element)).reduce((a, b) => a + b, 0) / json.length
    return isNaN(value) ? 0 : Math.round(value)
  }

  medianOfPercentagesElement(element: LoiElement): number {
    let value = element.properties.map(property => this.percentCorrectProperty(property)).reduce((a, b) => a + b, 0) / element.properties.length
    return isNaN(value) ? 0 : Math.round(value)
  }

  percentCorrectProperty(property: LoiProperties): number {
    let value = property.guidListCorrect.length / (property.guidListCorrect.length + property.guidListFail.length) * 100
    return isNaN(value) ? 0 : Math.round(value)
  }


  getFoundedGL(): number {
    let gl = 0;
    for (let result of this.selectedCheck?.partialServiceNames!) {
      if (result.substring(0, 2) == "GL") {
        gl++;
      }
    }
    return gl;
  }

  getFoundedBL(): number {
    let bl = 0;
    for (let result of this.selectedCheck?.partialServiceNames!) {
      if (result.substring(0, 2) == "BL") {
        bl++;
      }
    }
    return bl;
  }

  getSucceededPartialServices(): number {
    // return this.selectedCheck?.checkResults?.filter(result => result.succeed == true).length!;
    return 0
  }

  getFailedPartialServices(): number {
    return this.getFoundedBL() + this.getFoundedGL() - this.getSucceededPartialServices();
  }


  setSelectedCheck(check: any): void {
    this.selectedCheck = check;
  }


  navigateToTaskBoard(): void {
    this.router.navigate(['/tasks/list']);
  }

  navigateToFiles(): void {
    this.router.navigate(['/projects/' + this.project.id + '/files']);
  }

  navigateToProjectSettings(): void {
    this.router.navigate(['/projects/' + this.project.id + '/settings']);
  }

  navigateToReports(): void {
    this.router.navigate(['/projects/' + this.project.id + '/reports']);
  }

  /** BIC Katalog – Einstieg, wenn noch keine Prüfung ausgeführt wurde. */
  navigateToCatalog(): void {
    this.router.navigate(
      ['/projects/' + this.project.id + '/my-checks'],
      {queryParams: {tab: 'catalog'}}
    );
  }

  navigateToHOAICheck() {
    this.allInCheckNavigation.startHoaiCheck();
  }

  navigateToQualityCheck() {
    this.router.navigate(['/projects/' + this.project.id + '/quality']);
  }

  navigateToIfcCheck() {
    this.router.navigate(['/projects/' + this.project.id + '/reports/ifc/' + this.selectedCheck?.id]);
  }

  navigateToBIMBasisCheck() {
    this.router.navigate(['/projects/' + this.project.id + '/bim-basis-check']);
  }

  navigateToLOICheck() {
    this.allInCheckNavigation.startLoiplusCheck();
  }

  navigateToSpecificHOAICheck(check: string) {
    this.router.navigate(['/projects/' + this.project.id + '/all-in-check/hoai/' + check]);
  }

  navigateToSpecificQualityCheck(check: string) {
    this.router.navigate(['/projects/' + this.project.id + '/quality/' + check + '/report']);
  }

  navigateToSpecificBIMBasisCheck(check: string) {
    this.router.navigate(['/projects/' + this.project.id + '/reports/bim-base/' + check]);
  }

  navigateToSpecificLOICheck(check: string) {
    this.router.navigate(['/projects/' + this.project.id + '/reports/ifc/' + check]);
  }


  // options
  showXAxis = true;
  showYAxis = true;
  gradient = false;
  showGridLines = false;
  rotateXAxisTicks = false;
  noBarWhenZero = false;
  showDataLabel = false;
  showLegend = true;
  showXAxisLabel = true;
  xAxisLabel = 'Country';
  showYAxisLabel = true;
  yAxisLabel = 'Population';

  // options for pie-chart
  showLegendPie = false;
  showLabelsPie = true;
  isDoughnutPie = true;

  faLinkIcon = faChevronRight;
  faDropDownIcon = faChevronDown;


  protected readonly Object = Object;
  protected readonly faEllipsisVertical = faEllipsisVertical;
  protected readonly faCube = faCube;
  protected readonly faInfo = faInfo;
  protected readonly faBadgeCheck = faBadgeCheck;
  protected readonly faInfoCircle = faInfoCircle;
  protected readonly faClipboardCheck = faClipboardCheck;
  protected readonly faMemoCircleCheck = faMemoCircleCheck;
  protected readonly faIfc = faIfc;
}
